import { append, pipe, propEq, reject, assocPath, pathOr, concat, map, indexBy, prop, reduce, mergeWith, values, merge } from 'ramda';
import actionTypes from '../actions/reduxActions/actionTypes';

const getGroupElementById = (id, allGroupElements) => allGroupElements.find(groupelement => groupelement.id === id);

const updateElement = (state, groupelementId, manipulationFunction) => {
    const groupelement = getGroupElementById(groupelementId, state);
    const newGroupelementData = manipulationFunction(groupelement);
    return state.map(
        m => m.id === groupelementId ? newGroupelementData : m,
    );
};

const updateGroupElement = (groupElement, state) => pipe(
    reject(
        // remove current matching element
        propEq('id', groupElement.id),
    ),
    // replace with / add new group element
    append(groupElement),
)(state);

export const deleteAuthorizationGroup = ({ elements, groupElementId, index }) => updateElement(
    elements,
    groupElementId,
    (groupElement) => {
        const newAuthorizationGroups = groupElement.authorization.groups.filter((group, groupIndex) => groupIndex !== index);
        return assocPath(['authorization', 'groups'], newAuthorizationGroups.length > 0 ? newAuthorizationGroups : null, groupElement);
    },
);

export const deleteAuthorizationUser = ({ elements, groupElementId, index }) => updateElement(
    elements,
    groupElementId,
    (groupElement) => {
        const newAuthorizationUsers = groupElement.authorization.users.filter((user, userIndex) => userIndex !== index);
        return assocPath(['authorization', 'users'], newAuthorizationUsers.length > 0 ? newAuthorizationUsers : null, groupElement);
    },
);

export const changeAuthorizationGroupAttribute = ({ elements, groupElementId, groupAttributeId, index }) => updateElement(
    elements,
    groupElementId,
    groupElement => assocPath(
        ['authorization', 'groups', index, 'attribute'], groupAttributeId, groupElement,
    ),
);

export const changeAuthorizationUserAttribute = ({ elements, groupElementId, userAttributeId, index }) => updateElement(
    elements,
    groupElementId,
    groupElement => assocPath(
        ['authorization', 'users', index, 'attribute'], userAttributeId, groupElement,
    ),
);

export const addSelectedUsersOrGroups = ({ elements, groupElementId, selections, type }) => updateElement(
    elements,
    groupElementId,
    (groupElement) => {
        const usersOrGroupsToAdd = selections.map((selection) => {
            const friendlyNameProperty = selection.properties.find(
                property => property.developerName === 'FriendlyName',
            );
            const contentOrEmptyString = pathOr('', ['contentValue']);
            const friendlyName = contentOrEmptyString(friendlyNameProperty);

            return { authenticationId: selection.externalId, attribute: -1, friendlyName };
        });

        return assocPath(['authorization', `${type}s`], concat(groupElement.authorization[`${type}s`] || [], usersOrGroupsToAdd), groupElement);
    },
);

export const setSelectedUsersOrGroups = ({ elements, groupElementId, selections, type }) => updateElement(
    elements,
    groupElementId,
    groupElement => assocPath(
        ['authorization', `${type}s`], selections, groupElement,
    ),
);

export const updateGroupElementDeveloperName = ({ elements, mapElement, developerName }) => {
    const updatedMapElement = assocPath(['developerName'], developerName, mapElement);
    return updateGroupElement(updatedMapElement, elements);
};

export const updateGroupElementComment = ({ elements, groupElement, comment }) => {
    const updatedMapElement = assocPath(['developerSummary'], comment, groupElement);
    return updateGroupElement(updatedMapElement, elements);
};

export const updateGroupElementAuthorizationServiceId = ({ elements, groupElement, serviceElementId }) => {
    const updatedMapElement = assocPath(['authorization', 'serviceElementId'], serviceElementId, groupElement);
    return updateGroupElement(updatedMapElement, elements);
};

export const updateGroupElementAuthenticationType = ({ elements, groupElement, authenticationType }) => {
    const updatedMapElement = assocPath(['authorization', 'globalAuthenticationType'], authenticationType, groupElement);
    return updateGroupElement(updatedMapElement, elements);
};

export const updateGroupElementAuthorizationSocialStream = ({ elements, groupElement, streamBehaviour }) => {
    const updatedMapElement = assocPath(['authorization', 'streamBehaviourType'], streamBehaviour, groupElement);
    return updateGroupElement(updatedMapElement, elements);
};

const moveElement = ({ elementId, x, y }, elements) => {
    const updatedElements = elements.map((element) => {
        if (element.id === elementId) {
            return {
                ...element,
                x,
                y,
            };
        }
        return element;
    });

    return updatedElements;
};

const groupElements = (elements = [], { type, payload }) => {

    switch (type) {

    case actionTypes.MAP_ELEMENT_REMOVE:
        return elements.filter(mapElement => mapElement.id !== payload);

    case actionTypes.GROUP_ELEMENT_SET:
        return updateGroupElement(payload, elements);

    case actionTypes.GROUP_ELEMENT_SET_ALL:
        return payload;

    case actionTypes.GROUP_ELEMENT_MOVE:
        return moveElement(payload, elements);

    case actionTypes.SWIMLANE_ADD_AUTHORIZATION_USERS_GROUPS_SELECTION:
        return addSelectedUsersOrGroups({ elements, ...payload });

    case actionTypes.SWIMLANE_SET_AUTHORIZATION_USERS_GROUPS_SELECTION:
        return setSelectedUsersOrGroups({ elements, ...payload });

    case actionTypes.SWIMLANE_CHANGE_AUTHORIZATION_GROUP_ATTRIBUTE:
        return changeAuthorizationGroupAttribute({ elements, ...payload });

    case actionTypes.SWIMLANE_DELETE_AUTHORIZATION_GROUP:
        return deleteAuthorizationGroup({ elements, ...payload });

    case actionTypes.SWIMLANE_CHANGE_AUTHORIZATION_USER_ATTRIBUTE:
        return changeAuthorizationUserAttribute({ elements, ...payload });

    case actionTypes.SWIMLANE_DELETE_AUTHORIZATION_USER:
        return deleteAuthorizationUser({ elements, ...payload });

    case actionTypes.MAP_ELEMENT_SET_DEVELOPER_NAME:
        return updateGroupElementDeveloperName({ elements, ...payload });

    case actionTypes.SWIMLANE_SET_COMMENT:
        return updateGroupElementComment({ elements, ...payload });

    case actionTypes.SWIMLANE_SET_AUTHORIZATION_SERVICE_ID:
        return updateGroupElementAuthorizationServiceId({ elements, ...payload });

    case actionTypes.SWIMLANE_SET_AUTHORIZATION_SOCIAL_STREAM:
        return updateGroupElementAuthorizationSocialStream({ elements, ...payload });

    case actionTypes.SWIMLANE_SET_AUTHORIZATION_TYPE:
        return updateGroupElementAuthenticationType({ elements, ...payload });

    default:
        return elements;
    }
};

export default groupElements;
